import {
  ActivityOrderBy,
  Issue,
  IssueMessageType,
  Media,
  Message,
  Name,
  formatShortDate,
  formatShortDateOrSameDay,
  formatShortDateTimeOrSameDay,
  formatShortDateAndTimeOrSameDay,
  getFullName,
  getEffectiveCreationDate,
} from '@gripp/shared-logic';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { Colors } from '../../themes';

export type IssueMessageHeader = {
  updatedBy: string;
  updatedAt: string;
  fontStyle: StyleProp<TextStyle>;
  avatarSize: number;
  avatar?: Media;
  avatarIcon?: string;
  avatarIconStyle?: StyleProp<ViewStyle>;
  avatarIconColor?: string;
};

export const AnonymousId = '00000000-0000-0000-0000-000000000000';
export const AnonymousName = 'Anonymous';
export const FunctionId = '99999999-9999-9999-9999-999999999999';
export const SystemName = 'Gripp';

export type IssueAuditData = {
  who: string;
  when: string;
};
export const getIssueAuditData = (
  issue: Issue & { latestMessage?: any },
  orderBy: ActivityOrderBy
): IssueAuditData => {
  const isWeb = Platform.OS === 'web';

  let auditPrefix: string;
  let auditBy: any;
  let auditWhen: any;

  switch (orderBy) {
    case ActivityOrderBy.MostRecentReported:
    case ActivityOrderBy.CreationDate:
      auditPrefix = 'created';
      auditBy = (issue as any)[`${auditPrefix}By`];
      // Use the computed effective creation date from backend
      auditWhen =
        (issue as any).effectiveCreationDate ||
        (issue as any)[`${auditPrefix}At`];
      break;
    case ActivityOrderBy.ReminderDate:
      const nextReminderDate = (issue as any).nextReminderDate as
        | string
        | undefined;
      if (!nextReminderDate) {
        return { who: '------', when: '------' };
      }
      auditPrefix = 'created';
      auditBy = (issue as any)[`${auditPrefix}By`];
      auditWhen = nextReminderDate;
      break;
    default:
      auditPrefix = 'updated';
      auditBy = (issue as any)[`${auditPrefix}By`];
      auditWhen = (issue as any)[`${auditPrefix}At`];
      break;
  }

  let who;

  if (!auditBy) {
    who = AnonymousName;
  } else {
    who = getIssueFullName(auditBy.id, auditBy.name || undefined);
  }

  return {
    who,
    when:
      orderBy === ActivityOrderBy.ReminderDate
        ? formatShortDate(auditWhen)
        : !isWeb
        ? formatShortDateOrSameDay(auditWhen)
        : formatShortDateAndTimeOrSameDay(auditWhen),
  };
};

export const getIssueMessageTextStyle = (
  message: Message
): StyleProp<TextStyle> | undefined => {
  return message.type === IssueMessageType.UserMessage
    ? undefined
    : styles.systemText;
};

export const getIssueMessageHeader = (
  issueMessage: Message
): IssueMessageHeader => {
  const header: Partial<IssueMessageHeader> = {
    avatarSize: 34,
    updatedAt: formatShortDateTimeOrSameDay(issueMessage.updatedAt),
  };

  if (
    issueMessage.type === IssueMessageType.UserMessage &&
    ![AnonymousId, FunctionId].includes(issueMessage.updatedBy.id!)
  ) {
    assignUserHeader(header, issueMessage);
  } else {
    assignSystemHeader(header, issueMessage);
  }

  return header as IssueMessageHeader;
};

export const getIssueFullName = (
  id: string | undefined,
  name: Name | undefined
) => {
  switch (id) {
    case AnonymousId:
      return AnonymousName;
    case FunctionId:
      return SystemName;
    default:
      return name ? getFullName(name) : '';
  }
};

const assignUserHeader = (
  header: Partial<IssueMessageHeader>,
  issueMessage: Message
): void => {
  header.updatedBy = getFullName(issueMessage.updatedBy.name!);
  header.avatar = issueMessage.updatedBy.avatar;
};

const assignSystemHeader = (
  header: Partial<IssueMessageHeader>,
  issueMessage: Message
): void => {
  header.fontStyle = styles.systemText;
  header.avatarIcon = getSystemAvatarIcon(issueMessage);
  header.avatarIconStyle = styles.systemAvatar;
  header.avatarIconColor = Colors.white;
  header.updatedBy = getSystemUpdatedBy(issueMessage);
};

const getSystemUpdatedBy = (issueMessage: Message): string => {
  return issueMessage.updatedBy.id === AnonymousId &&
    issueMessage.type === IssueMessageType.UserMessage
    ? AnonymousName
    : SystemName;
};

const getSystemAvatarIcon = (issueMessage: Message): string => {
  return issueMessage.updatedBy.id === AnonymousId &&
    issueMessage.type === IssueMessageType.UserMessage
    ? 'incognito'
    : 'robot';
};

const styles = StyleSheet.create({
  systemText: {
    color: Colors.grayText,
  },
  systemAvatar: {
    backgroundColor: Colors.grayText,
  },
});
